import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import logo from "../../images/logo.png";

const Resume = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [isSigned, setIsSigned] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeletingFileId, setIsDeletingFileId] = useState(null);
  const fileInputRef = React.useRef(null);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const fetchResume = useCallback(async () => {
    if (!userId) return;

    try {
      const res = await axios.get(`${API_BASE}/api/resume/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      setUploadedFiles(res.data?.files || []);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error("Fetch failed", err);
      }
      setUploadedFiles([]);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchResume();
  }, [fetchResume]);

  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const TASK_ID = 25;
        const idKey = String(TASK_ID);
        const nameKey = "resume";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Error checking signature status:", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  const handleSelectFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    const invalidFile = selected.find((file) => !allowedTypes.includes(file.type));

    if (invalidFile) {
      alert("Only PDF, JPG, or PNG files are allowed!");
      e.target.value = "";
      return;
    }

    setFiles((prev) => {
      const existingKeys = new Set(
        prev.map((file) => `${file.name}_${file.size}_${file.lastModified}`),
      );
      const next = [...prev];

      selected.forEach((file) => {
        const key = `${file.name}_${file.size}_${file.lastModified}`;
        if (!existingKeys.has(key)) {
          existingKeys.add(key);
          next.push(file);
        }
      });

      return next;
    });

    e.target.value = "";
  };

  const removePendingFile = (fileToRemove) => {
    setFiles((prev) =>
      prev.filter(
        (file) =>
          !(
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size &&
            file.lastModified === fileToRemove.lastModified
          ),
      ),
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      alert("Unable to find user.");
      return;
    }

    if (files.length === 0 && uploadedFiles.length === 0) {
      alert("Please upload at least one PDF, JPG, or PNG file.");
      return;
    }

    const formData = new FormData();
    formData.append("userId", userId);
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      setIsSaving(true);
      await axios.post(`${API_BASE}/api/resume/upload?userId=${userId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await fetchResume();
      alert("Resume uploaded successfully!");
    } catch (err) {
      console.error("Upload error:", err);
      alert(err.response?.data?.message || "Upload failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUploadedFile = async (file) => {
    if (typeof file.id !== "number") {
      alert("This older resume file cannot be removed until it is migrated.");
      return;
    }

    if (!window.confirm(`Delete uploaded file "${file.original_name}"?`)) return;

    try {
      setIsDeletingFileId(file.id);
      await axios.delete(`${API_BASE}/api/resume/file/${file.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setUploadedFiles((prev) => prev.filter((f) => f.id !== file.id));
    } catch (err) {
      console.error("Resume file delete error:", err);
      alert(err.response?.data?.message || "Delete failed.");
    } finally {
      setIsDeletingFileId(null);
    }
  };

  const handleCancel = () => {
    navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome");
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Resume</span>
        <button className="btn btn-secondary" onClick={handleCancel}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Resume</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label>Select File(s) (PDF / JPG / PNG) *</label>
            <input
              type="file"
              className="form-control"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              ref={fileInputRef}
              onChange={handleSelectFiles}
              disabled={(!isAdmin && isSigned) || isSaving}
            />
          </div>

          {files.length > 0 && (
            <div className="mt-4 p-3 bg-light rounded border">
              <div className="text-muted mb-2">Files Ready To Save</div>

              {files.map((file, idx) => (
                <div
                  key={`${file.name}_${file.size}_${file.lastModified}_${idx}`}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <span>{file.name}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={(!isAdmin && isSigned) || isSaving}
                    onClick={() => removePendingFile(file)}
                    title="Delete"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          {uploadedFiles.length > 0 && (
            <div className="mt-4 p-3 bg-light rounded border">
              <div className="text-muted mb-2">Uploaded Files</div>

              {uploadedFiles.map((file) => (
                <div
                  key={file.id}
                  className="d-flex justify-content-between align-items-center mb-2"
                >
                  <div>
                    <a
                      href={`${API_BASE}/uploads/resumes/${file.path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {file.original_name}
                    </a>
                    <div className="text-muted">
                      Uploaded on:{" "}
                      {file.uploaded_at
                        ? new Date(file.uploaded_at).toLocaleString()
                        : "-"}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    disabled={
                      (!isAdmin && isSigned) ||
                      isDeletingFileId === file.id ||
                      typeof file.id !== "number"
                    }
                    onClick={() => handleDeleteUploadedFile(file)}
                    title="Delete"
                  >
                    {isDeletingFileId === file.id ? (
                      "Deleting..."
                    ) : (
                      <i className="bi bi-trash"></i>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="d-flex justify-content-end mt-4">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={(!isAdmin && isSigned) || isSaving}
            >
              {isSaving ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>

        <hr className="my-5" />

        {userId && (
          <>
            <SignatureManager
              taskName="resume"
              taskId={25}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />

            <ReviewManager
              taskName="resume"
              taskId={25}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default Resume;
